
import { useEffect, useRef, useState } from "react";
import MasterIcon from "../../../../../assets/images/icons/master-icon.svg";
import DownloadIcon from "../../../../../assets/images/icons/download.svg";
import { ReactComponent as MixIconSVG } from "../../../../../assets/images/icons/mix-icon.svg";
import { ReactComponent as WaveFormSVG } from "../../../../../assets/images/icons/wave.svg";
import MoreIcon from "../../../../../assets/images/icons/option.svg";
import { Music, Trash2, Edit3, Play, Pause, Share2, Link2, Music2Icon } from "lucide-react";
import { Tooltip } from "../../../../../components/Tooltip";
import DropdownMenu from "../../../../../components/DropdownMenu";
import { useDispatch, useSelector } from "react-redux";
import { extractFileName } from "../../../../../helper/extractFileName";
import { deleteMasterTrack, downloadMasterTrack, previewData, renameMasterTrack, uploadFileFromUrls } from "../../../../../redux/actions/audioEngineer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dialog from "../../../../../components/Dialogbox";
import SendFilePopup from "../../AudioEngineer/sections/SendFilePopup";
import WaveformPlayer from "../../AudioEngineer/sections/Waveform";
import { timeAgo, toMinSec } from "../../../../../helper/dataFormat";
import { useLocation, useNavigate } from "react-router-dom";
import { getFileExtensionRegex } from "../../../../../helper/utility";
import { useDeleteStem, useRenameStem, useRetreiveStemsByTrack } from "../../../../../queries/urstudio/mixenhancer.query";

function convertToMB(fileSize) {
    const MB = fileSize / (1024 * 1024);
    return MB.toFixed(2); // Returns with 2 decimal places
}

export const StemTrackTable = ({ selectedTracks, setSelectedTracks, stemTrackId, allStems }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false)
    const [hoveredTrack, setHoveredTrack] = useState(null);
    const [isMoving, setIsMoving] = useState(false)
    const [isMastering, setIsMastering] = useState(false)
    const [isPlaying, setIsPlaying] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [selectedTrack, setSelectedTrack] = useState(null)
    const [tempName, setTempName] = useState("");
    const [forceRender, setForceRender] = useState(false);
    const [progress, setProgress] = useState({});
    const [activeTrackId, setActiveTrackId] = useState(null);
    const audioRef = useRef(null);
    const progressRef = useRef({})
    const libraryData = useSelector((state) => state.audioEngineer)
    const { hasActiveSubscription } = useSelector((state) => state.billing);
    // const { data: stemsData, isLoading: stemsLoading, isError: stemsError } = useRetreiveStemsByTrack(stemTrackId);
    // const allStems = stemsData?.stems?.length === 0 
    //     ? Array.isArray(stemsDataFromTrack) 
    //         ? stemsDataFromTrack 
    //         : Object.values(stemsDataFromTrack || {})
    //     : stemsData?.stems || [];

    const handlePlay = (trackId) => {
        setActiveTrackId(trackId); // Only this track remains active
    };

    const handleCheckboxChange = (trackId) => {
        setSelectedTracks(prev => {
            if (prev.includes(trackId)) {
                return prev.filter(id => id !== trackId);
            } else {
                return [...prev, trackId];
            }
        });
    };

    const getMenuItems = (track) => [
        {
            label: "Rename...",
            icon: <Edit3 size={16} />,
            onClick: () => handleRename(track),
        },
        {
            label: "Share File",
            icon: <Share2 size={16} />,
            onClick: () => handleEmailDialog(track),
        },
        {
            label: "Get Link",
            icon: <Link2 size={16} />,
            onClick: () => {
                navigator.clipboard.writeText(track?.stemUrl);
            },
        },
        {
            label: "Move to Multitrack",
            icon: <Music2Icon size={16} />,
            onClick: () => {
                handleMoveToMultitrackSingle(track)
            },
        },
        {
            label: "Delete",
            icon: <Trash2 size={16} />,
            danger: true,
            onClick: () => handleDialog(track),
        },
    ];

    const downloadAudioFile = (url, format, fileName) => {
        const reqBody = {
            url: url,
            format: format,
            fileName: fileName,
        }
        dispatch(downloadMasterTrack(reqBody, () => { }))
    }

    const getDownloadItems = (track) => [
        {
            label: "Download as WAV file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.stemUrl, "wav", track.filename),
        },
        {
            label: "Download as MP3 file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.stemUrl, 'mp3', track.filename),
        },
        {
            label: "Download as M4A file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.stemUrl, 'm4a', track.filename),
        },
        {
            label: "Download as AAC file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.stemUrl, 'aac', track.filename),
        },
        {
            label: "Download as OGG file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.stemUrl, 'ogg', track.filename),
        },
        {
            label: "Download as AIFF file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.stemUrl, 'aiff', track.filename),
        }
    ];

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5; // show 5 rows per page (adjust as needed)

    const tracks = libraryData?.mastersLibrary?.data || [];
    const totalPages = Math.ceil(tracks.length / pageSize);
    const paginatedTracks = tracks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const renameMutation = useRenameStem()
    const deleteMutation = useDeleteStem()
    const { isPending } = deleteMutation

    const handleRename = (track) => {
        setEditingId(track._id);
        setTempName(extractFileName(track.filename));
    };

    const handleDelete = () => {
        if (!selectedTrack) return;
        deleteMutation.mutate(selectedTrack._id, {
            onSuccess: () => {
                // close dialog and remove from selection if present
                setIsDialogOpen(false);
                setSelectedTracks(prev => prev?.filter(id => id !== selectedTrack._id));
            }
        });
    };

    const handleDialog = (track) => {
        setSelectedTrack(track);
        setIsDialogOpen(!isDialogOpen)
    }

    const handleEmailDialog = (track) => {
        setSelectedTrack(track);
        setIsEmailDialogOpen(!isEmailDialogOpen)
    }

    // Stop any playing track when the user navigates to another page
    useEffect(() => {
        return () => {
            if (audioRef.current && !audioRef.current.paused) {
                try {
                    audioRef.current.pause();
                } catch (e) {
                    console.warn("Failed to stop library audio on page change", e);
                }
            }
        };
    }, []);

    // Stop any playing track when the route changes while the component is still mounted
    useEffect(() => {
        if (audioRef.current && !audioRef.current.paused) {
            try {
                audioRef.current.pause();
            } catch (e) {
                console.warn("Failed to stop library audio on route change", e);
            }
        }
        setIsPlaying(null);
    }, [location.pathname]);

    const handlePlayPause = (track) => {
        const trackIdentifier = track._id || track.id || null;

        if (isPlaying === trackIdentifier) {
            audioRef.current?.pause();
            setIsPlaying(null);
            return;
        }

        if (audioRef.current) {
            audioRef.current.pause();
        }

        audioRef.current = new Audio(track.stemUrl);
        audioRef.current.play();
        setIsPlaying(trackIdentifier);

        // update progress in ref only
        audioRef.current.ontimeupdate = () => {
            const percent =
                (audioRef.current.currentTime / audioRef.current.duration) * 100;
            progressRef.current[trackIdentifier] = percent;
            setForceRender((p) => !p);
        };

        // reset when track ends
        audioRef.current.onended = () => {
            setIsPlaying(null);
            progressRef.current[trackIdentifier] = 0;
            setForceRender((p) => !p);
        };
    };

    const handleMoveToMultitrackSingle = (track) => {
        if (!track) return;
        try {
            // map track to uploadedData shape expected by MultiTrack
            const mapped = {
                _id: track._id || track.id,
                filename: track.filename,
                originalUrl: track.stemUrl,
            };

            // merge with existing uploadedData
            let merged = [];
            // avoid duplicates by _id
            const exists = merged.some(i => String(i._id) === String(mapped._id));
            if (!exists) merged.push(mapped);

            const requestBody = {
                sourceUrls: [mapped.originalUrl]
            }
            dispatch(uploadFileFromUrls(requestBody, setIsMoving, 'mixing-engineer', navigate))
        } catch (e) {
            console.debug('Move to Multitrack failed', e);
        }
    }

    const handleMasterCreation = (track) => {
        if (!track) return;
        try {
            // map track to uploadedData shape expected by MultiTrack
            const mapped = {
                _id: track._id || track.id,
                filename: track.filename,
                originalUrl: track.stemUrl,
            };

            // merge with existing uploadedData
            let merged = [];
            // avoid duplicates by _id
            const exists = merged.some(i => String(i._id) === String(mapped._id));
            if (!exists) merged.push(mapped);
            const requestBody = {
                sourceUrls: [mapped.originalUrl]
            }
            dispatch(uploadFileFromUrls(requestBody, setIsMastering, 'mastering', navigate))
        } catch (e) {
            console.debug('Move to Multitrack failed', e);
        }
    }

    return (
        <>
            <tbody>
                {isMoving &&
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">Moving your track to Multitrack...</td>
                    </tr>
                }
                {isMastering &&
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">Processing your track for Master Release...</td>
                    </tr>
                }
                {allStems?.length === 0 && (
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">No data available</td>
                    </tr>
                )}
                {(allStems || [])?.map((track, idx) => {
                    const trackIdentifier = track._id || idx;
                    const isTrackPlaying = isPlaying === trackIdentifier;
                    const shouldShowButton = hoveredTrack === trackIdentifier || isTrackPlaying;

                    return (
                        <tr
                            key={trackIdentifier}
                            className="masters-library-table hover:bg-[#11175c] transition-colors rounded-lg"
                            onMouseEnter={() => setHoveredTrack(trackIdentifier)}
                            onMouseLeave={() => setHoveredTrack(null)}
                        >
                            <td className="p-2">
                                <input
                                    type="checkbox"
                                    checked={selectedTracks?.includes(trackIdentifier)}
                                    onChange={() => handleCheckboxChange(trackIdentifier)}
                                    className="rounded border-gray-400 bg-transparent text-[#FA6138] focus:ring-[#FA6138] cursor-pointer"
                                    style={{ backgroundColor: 'transparent' }}
                                />
                            </td>
                            <td className="p-2">
                                <div className="max-w-[300px] flex items-center gap-3">
                                    <div className="bg-[#FA6138] p-2 rounded flex items-center justify-center min-w-[35px] w-[35px] h-[35px]">
                                        {shouldShowButton ? (
                                            <button onClick={() => handlePlayPause(track)}>
                                                {isTrackPlaying ? (
                                                    <Pause size={20} color="#ffffff" />
                                                ) : (
                                                    <Play size={20} color="#ffffff" />
                                                )}
                                            </button>
                                        ) : (
                                            <Music size={20} color="#ffffff" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-white text-sm font-medium break-all">
                                            {(editingId === track._id || editingId === idx) ? (
                                                <input
                                                    type="text"
                                                    value={tempName}
                                                    autoFocus
                                                    onChange={(e) => setTempName(e.target.value)}
                                                    onBlur={() => {
                                                        // save changes when input loses focus
                                                        renameMutation.mutate({ id: track._id, body: { filename: tempName } })
                                                        setEditingId(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            renameMutation.mutate({ id: track._id, body: { filename: tempName } })
                                                            setEditingId(null);
                                                        }
                                                        if (e.key === "Escape") {
                                                            setEditingId(null); // cancel rename
                                                        }
                                                    }}
                                                    className="bg-transparent border-b border-gray-400 text-white outline-none"
                                                />
                                            ) : renameMutation.isPending && renameMutation.variables?.id === track._id ? (
                                                <span className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    Renaming...
                                                </span>
                                            ) : deleteMutation.isPending && deleteMutation.variables === track._id ? (
                                                <span className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    Deleting...
                                                </span>
                                            ) : (
                                                extractFileName(track.filename)
                                            )}
                                        </p>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-gray-300">
                                                    {getFileExtensionRegex(track?.stemUrl).toUpperCase()}
                                                </span>
                                                <span className="bg-white text-[#00067e] text-nowrap text-xs px-2 py-1 rounded-xl">{track?.stemName}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            {/* <td className="p-2">
                            <img
                                src={WaveFormImage}
                                className="h-4 sm:h-6"
                                alt="Waveform"
                            />
                        </td> */}
                            {/* Waveform with Progress */}
                            <td className="p-2">
                                <div className="relative w-32 sm:w-32 h-6 overflow-hidden">
                                    {/* <Tooltip
                                    text={"Generate Analysis Report"}
                                    style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-auto" }}
                                    position={idx === 0 ? "bottom" : "top"}
                                >
                                    <button>
                                        <MixIconSVG className="block h-[16px] mr-2" />
                                    </button>
                                </Tooltip> */}
                                    {/* <WaveformPlayer
                                    key={track._id}
                                    track={track}
                                    isActive={activeTrackId === track._id}
                                    onPlay={handlePlay}
                                /> */}
                                    {/* Base waveform (gray) */}
                                    <WaveFormSVG className="absolute top-0 left-0 w-full h-full opacity-30 ml-6" />
                                    {/* Progress waveform (white overlay) */}
                                    <div
                                        className="absolute top-0 left-0 h-full overflow-hidden transition-all duration-200"
                                        style={{ width: `${progress[idx + 1] || 0}%` }}
                                    >
                                        <WaveFormSVG className="w-full h-full" />
                                    </div>
                                </div>
                            </td>
                            <td className="p-2 text-white">{toMinSec(track?.duration) || "..."}</td>
                            <td className="p-2 text-gray-300">{timeAgo(track?.updatedAt) || "..."}</td>
                            <td className="p-2 text-white">{convertToMB(track?.fileSize) + "MB" || "..."}</td>
                            <td className="p-2">
                                <div className="flex items-center gap-2">
                                    {[MasterIcon, DownloadIcon, MoreIcon].map(
                                        (icon, i) => (
                                            <Tooltip
                                                text={
                                                    i === 0
                                                        ? "Master"
                                                        : i === 1
                                                            ? "Download"
                                                            : "More Options"
                                                }
                                                style={{
                                                    bg: "bg-white",
                                                    text: "text-theme_clr1",
                                                    width: "w-auto",
                                                }}
                                                position={idx === 0 ? "bottom-right" : i === 2 ? "top-right" : "top"}
                                            >
                                                {i === 0 ? (
                                                    <button onClick={() => handleMasterCreation(track)} className="bg-white flex items-center justify-center rounded-full text-black hover:bg-theme_clr2 h-[30px] w-[100px]">
                                                        <img src={icon} alt="" className="block mr-1" /> Master
                                                    </button>
                                                ) : i === 1 ? (
                                                    <DropdownMenu
                                                        button={
                                                            <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                                <img src={icon} alt="" className="block" />
                                                            </button>
                                                        }
                                                        items={getDownloadItems(track)}
                                                    />
                                                ) : i === 2 ? (
                                                    <DropdownMenu
                                                        button={
                                                            <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                                <img src={icon} alt="" className="block" />
                                                            </button>
                                                        }
                                                        items={getMenuItems(track)}
                                                    />
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            navigate('/admin/urstudio/audio-engineer')
                                                            dispatch(previewData({ ...track, previewUrl: track?.masteredUrl, isMastered: true, playNow: true }))
                                                        }}
                                                        key={i}
                                                        className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]"
                                                    >
                                                        <img src={icon} alt="" className="block" />
                                                    </button>
                                                )}
                                            </Tooltip>
                                        )
                                    )}
                                </div>
                            </td>
                        </tr>
                    )
                })}
            </tbody>
            {/*  Pagination Controls */}
            {/* {totalPages > 1 && (
                <tfoot>
                    <tr>
                        <td colSpan="7" className="p-4">
                            <div className="flex justify-center items-center gap-3 mt-4 text-white">
                                <button
                                    className="px-3 py-1 bg-theme_clr1 rounded disabled:opacity-50"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => p - 1)}
                                >
                                    Previous
                                </button>

                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`px-3 py-1 rounded ${currentPage === i + 1 ? "bg-theme_clr2 text-white" : "bg-theme_clr1"
                                            }`}
                                        onClick={() => setCurrentPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}

                                <button
                                    className="px-3 py-1 bg-theme_clr1 rounded disabled:opacity-50"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )} */}
            <Dialog
                title={"Delete Confirmation"}
                isOpen={isDialogOpen}
                hasActiveSubscription={hasActiveSubscription}
                onClose={handleDialog}
                onSave={handleDelete}
                leftButton={'No'}
                rightButton={'Yes'}
                isLoading={isPending}
            >
                <div className="mb-6">
                    <h3 className="font-medium text-theme_clr2 mb-3">
                        Are you sure, you want to delete this stem?
                    </h3>
                    <div className="bg-theme_clr1 text-gray-300 p-4 rounded-xl flex flex-col gap-3">
                        {isPending ?
                            <p>
                                Processing your request...
                            </p> :
                            <p>
                                It will be removed from database permanently and cannot be restored.
                            </p>
                        }
                    </div>
                </div>
            </Dialog>
            <SendFilePopup isOpen={isEmailDialogOpen} onClose={() => setIsEmailDialogOpen(false)} fileUrl={selectedTrack?.stemUrl} />
        </>
    );
};